import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  getMyJoinRequests,
  getPendingJoinRequests,
  createJoinRequest,
  approveJoinRequest,
  declineJoinRequest,
  deleteJoinRequest,
  type CreateJoinRequestInput,
  type ApproveJoinRequestInput,
  type DeclineJoinRequestInput,
} from '../services/api/joinRequests';
import { invalidateMany } from './queryUtils';

/**
 * Caches every approve/decline/delete join-request mutation must refresh:
 * the requester's own list, the manager queue, the approval-counts badge,
 * and — per F9-001 / F10-003 — the requester's "my projects" sidebar that
 * may surface pending requests. Create uses a smaller set (no
 * approvalCounts/my-projects yet) so it stays inline.
 *
 * Order matches the prior inline sequence in `useApproveJoinRequest` so
 * cache observers see invalidations in the same order; per
 * `invalidateMany`'s contract, TanStack invalidation is fire-and-forget
 * and prefix-matched, so siblings with reordered original sequences
 * (decline/delete) are still behaviour-preserving.
 */
function invalidateJoinRequestCaches(qc: QueryClient): void {
  invalidateMany(qc, [
    ['pendingJoinRequests'],
    ['myJoinRequests'],
    ['approvalCounts'],
    ['my-projects'],
  ]);
}

export function useMyJoinRequests(state?: string) {
  return useQuery({
    queryKey: ['myJoinRequests', state],
    queryFn: () => getMyJoinRequests(state),
  });
}

export function usePendingJoinRequests(scope?: 'all') {
  return useQuery({
    queryKey: ['pendingJoinRequests', scope ?? 'managed'],
    queryFn: () => getPendingJoinRequests(scope),
  });
}

export function useCreateJoinRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateJoinRequestInput) => createJoinRequest(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myJoinRequests'] });
      queryClient.invalidateQueries({ queryKey: ['pendingJoinRequests'] });
    },
  });
}

export function useApproveJoinRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: ApproveJoinRequestInput }) =>
      approveJoinRequest(id, input),
    onSuccess: (joinRequest) => {
      invalidateJoinRequestCaches(queryClient);
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      // F9-001: backend creates a projectAssignment row + the requester gains
      // membership in the project; invalidate the project's assignments tab
      // (the requester's "my projects" sidebar is already covered by the
      // shared helper above).
      if (joinRequest?.projectId != null) {
        queryClient.invalidateQueries({
          queryKey: ['projectAssignments', joinRequest.projectId],
        });
      }
    },
  });
}

export function useDeclineJoinRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: DeclineJoinRequestInput }) =>
      declineJoinRequest(id, input),
    onSuccess: () => {
      // F10-003: shared helper covers ['my-projects'] for forward-compat
      // with the v7.1 join-request UI that may surface pending requests
      // in the "my projects" sidebar — symmetric with approve/delete.
      invalidateJoinRequestCaches(queryClient);
    },
  });
}

export function useDeleteJoinRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteJoinRequest(id),
    onSuccess: () => {
      // F9-004: self-delete only succeeds for state='pending', so the row
      // disappears from both the requester's list and the manager queue +
      // approval-counts badge.
      // F10-003: ['my-projects'] symmetric with approve/decline.
      invalidateJoinRequestCaches(queryClient);
    },
  });
}
